"use client";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import {
  ChevronRight,
  Check,
  Loader2,
  Circle,
  Search,
  Sparkles,
  type LucideIcon,
} from "lucide-react";
import * as React from "react";

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

interface ChainOfThoughtContextValue {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

const ChainOfThoughtContext =
  React.createContext<ChainOfThoughtContextValue | null>(null);

// ---------------------------------------------------------------------------
// <ChainOfThought />
// ---------------------------------------------------------------------------

interface ChainOfThoughtProps extends React.ComponentProps<"div"> {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function ChainOfThought({
  children,
  className,
  open,
  defaultOpen = false,
  onOpenChange,
  ...props
}: ChainOfThoughtProps) {
  const [isOpen, setIsOpenState] = React.useState(defaultOpen);
  const actualOpen = open !== undefined ? open : isOpen;

  const setIsOpen = React.useCallback(
    (value: boolean) => {
      setIsOpenState(value);
      onOpenChange?.(value);
    },
    [onOpenChange]
  );

  return (
    <ChainOfThoughtContext.Provider value={{ isOpen: actualOpen, setIsOpen }}>
      <Collapsible
        open={actualOpen}
        onOpenChange={setIsOpen}
        className={cn("w-full", className)}
        {...props}
      >
        {children}
      </Collapsible>
    </ChainOfThoughtContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// <ChainOfThoughtHeader />
// ---------------------------------------------------------------------------

export function ChainOfThoughtHeader({
  children,
  className,
  ...props
}: React.ComponentProps<typeof CollapsibleTrigger>) {
  return (
    <CollapsibleTrigger
      className={cn(
        "group inline-flex items-center gap-2 rounded-md py-1.5 text-sm transition-colors",
        "text-[color:var(--foreground-muted)] hover:text-[color:var(--foreground)]",
        className
      )}
      {...props}
    >
      <ChevronRight className="h-3.5 w-3.5 shrink-0 text-[color:var(--foreground-muted)] transition-transform duration-200 group-data-[state=open]:rotate-90" />

      <Sparkles className="h-4 w-4 shrink-0 text-[color:var(--nt-primary)]" />

      <span className="font-medium text-[13px]">
        {children || "Cadeia de Pensamento"}
      </span>
    </CollapsibleTrigger>
  );
}

// ---------------------------------------------------------------------------
// <ChainOfThoughtContent />
// ---------------------------------------------------------------------------

export function ChainOfThoughtContent({
  className,
  children,
  ...props
}: React.ComponentProps<typeof CollapsibleContent>) {
  return (
    <CollapsibleContent
      className={cn(
        "overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down",
        className
      )}
      {...props}
    >
      <div className="mt-1 ml-[7px] pl-4 py-2 space-y-0">{children}</div>
    </CollapsibleContent>
  );
}

// ---------------------------------------------------------------------------
// <ChainOfThoughtStep />
// ---------------------------------------------------------------------------

type StepStatus = "complete" | "active" | "pending";

interface ChainOfThoughtStepProps extends React.ComponentProps<"div"> {
  icon?: LucideIcon;
  label: string;
  description?: string;
  status?: StepStatus;
  isLast?: boolean;
}

export function ChainOfThoughtStep({
  label,
  description,
  status = "complete",
  isLast = false,
  className,
  children,
  ...props
}: ChainOfThoughtStepProps) {
  return (
    <div
      className={cn("relative flex items-start gap-3 pb-3", className)}
      {...props}
    >
      {/* Vertical connector line */}
      {!isLast && (
        <div
          className={cn(
            "absolute left-[11px] top-6 bottom-0 w-px",
            status === "complete"
              ? "bg-emerald-300/60 dark:bg-emerald-700/40"
              : status === "active"
              ? "bg-[color:var(--nt-primary)]/30"
              : "bg-[color:var(--card-border)]"
          )}
        />
      )}

      {/* Step icon */}
      <div
        className={cn(
          "relative z-10 flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full border",
          status === "complete" &&
            "border-emerald-300 bg-emerald-50 dark:border-emerald-700 dark:bg-emerald-950/40",
          status === "active" &&
            "border-[color:var(--nt-primary)]/40 bg-[color:var(--nt-primary)]/10",
          status === "pending" &&
            "border-[color:var(--card-border)] bg-[color:var(--card-bg)]"
        )}
      >
        {status === "complete" ? (
          <Check className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
        ) : status === "active" ? (
          <Loader2 className="h-3 w-3 animate-spin text-[color:var(--nt-primary)]" />
        ) : (
          <Circle className="h-2.5 w-2.5 text-[color:var(--foreground-muted)]" />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 pt-0.5">
        <div
          className={cn(
            "text-[13px] font-medium leading-tight",
            status === "complete" && "text-[color:var(--foreground)]",
            status === "active" && "text-[color:var(--nt-primary)]",
            status === "pending" && "text-[color:var(--foreground-muted)]"
          )}
        >
          {label}
        </div>
        {description && (
          <p
            className={cn(
              "mt-0.5 text-xs leading-relaxed",
              status === "pending"
                ? "text-[color:var(--foreground-muted)]/50"
                : "text-[color:var(--foreground-muted)]"
            )}
          >
            {description}
          </p>
        )}
        {children}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// <ChainOfThoughtSearchResults /> & <ChainOfThoughtSearchResult />
// ---------------------------------------------------------------------------

export function ChainOfThoughtSearchResults({
  className,
  children,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      className={cn("mt-1.5 flex flex-wrap gap-1.5", className)}
      {...props}
    >
      {children}
    </div>
  );
}

export function ChainOfThoughtSearchResult({
  className,
  children,
  ...props
}: React.ComponentProps<"span">) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5",
        "text-[10px] font-medium",
        "bg-[color:var(--card-bg)] text-[color:var(--foreground-muted)]",
        "border border-[color:var(--card-border)]",
        className
      )}
      {...props}
    >
      <Search className="h-2.5 w-2.5" />
      {children}
    </span>
  );
}
